/**
 * Pure helpers for the date and time popovers (STYLE_GUIDE "Date and time pickers", B-153).
 */

export const MIN_YEAR = 1900;
export const MAX_YEAR = 2100;

export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Same day of month n months away, clamped to that month's length (31 Jan + 1 → 28/29 Feb). */
export const addMonths = (d: Date, n: number) => {
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last));
};

export const clampYear = (d: Date) => {
  if (d.getFullYear() < MIN_YEAR) return new Date(MIN_YEAR, 0, 1);
  if (d.getFullYear() > MAX_YEAR) return new Date(MAX_YEAR, 11, 31);
  return d;
};

/** The 6×7 grid shown for a month: Sunday-first weeks, padded with the neighbouring months' days. */
export function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** "Sun, 1 Nov 2026" */
export const formatPillDate = (d: Date) =>
  `${d.toLocaleDateString('en-GB', { weekday: 'short' })}, ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })} ${d.getFullYear()}`;

/** "HH:MM" or "HH:MM:SS" → seconds since midnight; null if malformed. */
export function toSeconds(t?: string | null): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec((t || '').trim());
  if (!m) return null;
  const [h, mi, s] = [Number(m[1]), Number(m[2]), Number(m[3] || 0)];
  if (h > 23 || mi > 59 || s > 59) return null;
  return h * 3600 + mi * 60 + s;
}

export const fromSeconds = (n: number) => {
  const v = ((Math.round(n) % 86400) + 86400) % 86400;
  const p = (x: number) => String(x).padStart(2, '0');
  return `${p(Math.floor(v / 3600))}:${p(Math.floor((v % 3600) / 60))}:${p(v % 60)}`;
};

/** Halfway from sunrise to sunset: the apparent noon. */
export function midday(sunrise?: string, sunset?: string): string | null {
  const a = toSeconds(sunrise), b = toSeconds(sunset);
  if (a === null || b === null || b <= a) return null;
  return fromSeconds((a + b) / 2);
}

export type TimePart = 'h' | 'm' | 's';
const LIMIT: Record<TimePart, number> = { h: 24, m: 60, s: 60 };

/** Step one part of "HH:MM:SS" by delta, wrapping within that part (23→00, 59→00); the others stay. */
export function stepPart(time: string, part: TimePart, delta: number): string {
  const [h, m, s] = (fromSeconds(toSeconds(time) ?? 0)).split(':').map(Number);
  const v = { h, m, s };
  v[part] = (((v[part] + delta) % LIMIT[part]) + LIMIT[part]) % LIMIT[part];
  return fromSeconds(v.h * 3600 + v.m * 60 + v.s);
}

/** Set one part from typed digits; out-of-range input is clamped to the part's maximum. */
export function setPart(time: string, part: TimePart, value: number): string {
  const [h, m, s] = (fromSeconds(toSeconds(time) ?? 0)).split(':').map(Number);
  const v = { h, m, s };
  v[part] = Math.max(0, Math.min(LIMIT[part] - 1, Math.floor(value)));
  return fromSeconds(v.h * 3600 + v.m * 60 + v.s);
}
